import api from './api';
import type { StreamCallbacks } from './chatService';

/**
 * Onboarding conversacional — construir a proposta pelo chat.
 * Spec: docs/superpowers/specs/2026-09-08-chat-onboarding-proposta-completa.md
 */

export interface OnboardingSession {
  state: string;
  step: number;
  total_steps: number;
  proposal_id: number | null;
  autonomo: boolean;
  data: Record<string, unknown>;
  derived: Record<string, unknown>;
}

async function streamSSE(
  path: string,
  body: Record<string, unknown>,
  callbacks: StreamCallbacks,
  signal?: AbortSignal,
): Promise<void> {
  const baseURL = api.defaults.baseURL || '';
  const token = localStorage.getItem('prainte_token');
  const response = await fetch(`${baseURL}${path}`, {
    method: 'POST',
    signal,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.detail || `HTTP ${response.status}`);
  }
  const reader = response.body?.getReader();
  if (!reader) throw new Error('Stream não disponível');
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try {
        const p = JSON.parse(line.slice(6));
        if (p.type === 'chunk') callbacks.onChunk(p.content);
        else if (p.type === 'state') callbacks.onState?.(p);
        else if (p.type === 'tool_call') callbacks.onToolCall?.(p);
        else if (p.type === 'tool_result') callbacks.onToolResult?.(p);
        else if (p.type === 'created_proposal_id') callbacks.onProposalCreated?.(p.proposal_id);
        else if (p.type === 'done') callbacks.onDone?.(p.response, {
          pending_call: p.pending_call ?? null,
          created_proposal_id: p.created_proposal_id ?? null,
          state: p.state,
          step: p.step,
        });
        else if (p.type === 'error') callbacks.onError?.(p.content);
      } catch { /* ignore unknown */ }
    }
  }
}

export const chatOnboardingService = {
  /** Sessão da proposta, ou a pré-proposta (proposalId omitido). null se não houver. */
  getSession: async (proposalId?: number | null): Promise<OnboardingSession | null> => {
    try {
      const path = proposalId ? `/chat/onboarding/${proposalId}` : '/chat/onboarding';
      const res = await api.get<OnboardingSession>(path);
      return res.status === 204 ? null : res.data;
    } catch {
      return null;
    }
  },

  /** Confirma (ou recusa) a tool pendente. Resposta é SSE — mesmos callbacks do chat. */
  confirm: async (
    params: { proposalId?: number | null; callId: string; decision: 'approve' | 'reject'; options?: Record<string, unknown> },
    callbacks: StreamCallbacks,
    signal?: AbortSignal,
  ): Promise<void> => {
    await streamSSE('/chat/onboarding/confirm', {
      proposal_id: params.proposalId ?? null,
      call_id: params.callId,
      decision: params.decision,
      options: params.options ?? {},
    }, callbacks, signal);
  },

  setMode: async (autonomo: boolean, proposalId?: number | null): Promise<OnboardingSession | null> => {
    try {
      const res = await api.post<OnboardingSession>('/chat/onboarding/mode', {
        proposal_id: proposalId ?? null,
        autonomo,
      });
      return res.data;
    } catch {
      return null;
    }
  },

  abort: async (proposalId?: number | null): Promise<void> => {
    try {
      await api.post('/chat/onboarding/abort', { proposal_id: proposalId ?? null });
    } catch { /* best-effort */ }
  },
};
